import { type RefObject, useEffect } from "react";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "details",
  // The payment stages embed their card fields in a hosted frame (Stripe,
  // FreedomPay), which the guest has to be able to Tab to.
  "iframe",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

function isHidden(element: HTMLElement): boolean {
  if (element.hasAttribute("hidden")) return true;
  const style = getComputedStyle(element);
  return style.display === "none" || style.visibility === "hidden";
}

function focusableWithin(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (element) => !element.hasAttribute("inert") && element.getAttribute("aria-hidden") !== "true" && !isHidden(element),
  );
}

function createSentinel(onFocus: () => void): HTMLSpanElement {
  const sentinel = document.createElement("span");
  sentinel.tabIndex = 0;
  sentinel.setAttribute("aria-hidden", "true");
  sentinel.dataset.gcFocusSentinel = "";
  sentinel.addEventListener("focus", onFocus);
  return sentinel;
}

/**
 * Modal focus behaviour for a container that is not a native `<dialog>`: focus
 * moves into it when it opens, Tab and Shift+Tab cycle within it, and focus
 * returns to where it came from — or to `returnFocusRef`, for a trigger that
 * only exists once the container has closed — when it closes.
 *
 * Tab presses inside a cross-origin frame — the hosted payment fields — never
 * reach this document, so the cycle is closed with a tabbable sentinel either
 * side of the container as well: whichever one the browser moves focus to on
 * the way out of the frame sends it back round.
 *
 * `initialFocusSelector` picks where focus lands on open: the first focusable
 * element matching it rather than the first focusable of any kind. A caller
 * that opens mid-journey uses it to land on the screen's own content — the
 * widget's stages use it to start on the first editable field — instead of the
 * chrome (the back control) that happens to lead the container's tab order.
 * When the selector matches nothing the container itself is focused, so no
 * unrelated control appears pre-selected; omitted, the first focusable
 * element is focused as before.
 */
export function useModalFocus(
  active: boolean,
  containerRef: RefObject<HTMLElement | null>,
  returnFocusRef?: RefObject<HTMLElement | null>,
  initialFocusSelector?: string,
): void {
  useEffect(() => {
    const container = containerRef.current;
    if (!active || !container) return;

    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const focusableOnOpen = focusableWithin(container);
    const initial = initialFocusSelector
      ? focusableOnOpen.find((element) => element.matches(initialFocusSelector))
      : focusableOnOpen[0];
    (initial ?? container).focus();

    const focusEdge = (edge: "first" | "last") => {
      const focusable = focusableWithin(container);
      const target = edge === "first" ? focusable[0] : focusable[focusable.length - 1];
      (target ?? container).focus();
    };
    const leadingSentinel = createSentinel(() => focusEdge("last"));
    const trailingSentinel = createSentinel(() => focusEdge("first"));
    container.parentNode?.insertBefore(leadingSentinel, container);
    container.parentNode?.insertBefore(trailingSentinel, container.nextSibling);

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Tab") return;

      const focusable = focusableWithin(container);
      if (focusable.length === 0) {
        event.preventDefault();
        container.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const current = document.activeElement;
      const outside = !(current instanceof Node) || !container.contains(current);

      if (event.shiftKey && (outside || current === first)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (outside || current === last)) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
      leadingSentinel.remove();
      trailingSentinel.remove();
      // The cleanup runs after the closed state has rendered, so a trigger that
      // the closed state brings back is in the document by now.
      (returnFocusRef?.current ?? previouslyFocused)?.focus();
    };
  }, [active, containerRef, returnFocusRef, initialFocusSelector]);
}
